import React from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { navigateToSection } from "../../lib/sectionNavigation";
import { sectionLinks } from "../../lib/seo";

const NAV_LINKS = sectionLinks.filter((link) => link.id !== "companies");

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState("home");
  const location = useLocation();
  const navigate = useNavigate();

  const handleSectionNavigation = (sectionId) => {
    navigateToSection({
      sectionId,
      pathname: location.pathname,
      navigate,
      setActiveSection,
    });
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (location.pathname.startsWith("/blogs/")) {
      setActiveSection("blogs");
      return undefined;
    }

    if (location.hash) {
      setActiveSection(location.hash.replace("#", ""));
      return undefined;
    }

    setActiveSection("home");
    return undefined;
  }, [location.hash, location.pathname]);

  React.useEffect(() => {
    if (location.pathname !== "/") {
      return undefined;
    }

    const handleScroll = () => {
      const sections = NAV_LINKS.map((link) => document.getElementById(link.id));
      const scrollPosition = window.scrollY + 120;

      sections.forEach((section, index) => {
        if (!section) {
          return;
        }

        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(NAV_LINKS[index].id);
        }
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  return (
    <nav
      className="fixed left-0 right-0 top-0 z-50 border-b border-[#2a3a5a] bg-[#0f1a2e]/95 text-white shadow-lg backdrop-blur-md"
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <a
            href="/"
            onClick={(event) => {
              event.preventDefault();
              handleSectionNavigation("home");
            }}
            className="flex items-center gap-3 transition-opacity hover:opacity-90"
            data-testid="logo"
          >
            <img
              src="/images/logo.jpeg"
              alt="Vinayak Enterprise industrial pipeline contractor logo"
              className="h-10 w-10 rounded object-cover"
              width="40"
              height="40"
              decoding="async"
            />
            <span className="text-xl font-bold text-[#c23a4a]">Vinayak Enterprise</span>
          </a>

          <div className="hidden items-center space-x-6 md:flex lg:space-x-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={(event) => {
                  event.preventDefault();
                  handleSectionNavigation(link.id);
                }}
                className={`text-sm transition-colors duration-200 lg:text-base ${
                  activeSection === link.id
                    ? "font-semibold text-[#c23a4a]"
                    : "text-gray-300 hover:text-[#c23a4a]"
                }`}
                data-testid={`nav-${link.id}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <button
            className="rounded-lg p-2 transition-colors hover:bg-[#1a2744] md:hidden"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isOpen && (
          <div className="border-t border-[#2a3a5a] py-4 md:hidden">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={(event) => {
                  event.preventDefault();
                  handleSectionNavigation(link.id);
                }}
                className={`block w-full rounded-lg px-4 py-3 text-left transition-colors ${
                  activeSection === link.id
                    ? "bg-[#1a2744] font-semibold text-[#c23a4a]"
                    : "text-gray-300 hover:bg-[#1a2744] hover:text-[#c23a4a]"
                  }`}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
